/** WxmPages — Pages & subpages builder for the Website Experience Manager.
 * Split out of WebsiteExperienceManager.jsx (babel-loader stack limits on huge files). */
import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Plus, Trash2, Pencil, Eye, ExternalLink, Loader2, X,
  FileText, Send, Copy, Save, BookOpenCheck, GripVertical,
  ChevronUp, ChevronDown, CornerDownRight,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MediaEmbed, mediaHtml, mediaKind } from "@/lib/wxmMedia";

function EmptyHint({ title, hint }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-500"><Sparkles size={20}/></div>
      <div className="text-base font-bold text-[#0F2042]">{title}</div>
      <div className="mt-1 text-sm text-slate-500">{hint}</div>
    </div>
  );
}

function flatTree(rows) {
  const byId = Object.fromEntries(rows.map((r) => [r.id, r]));
  const roots = rows.filter((r) => !r.parent_id || !byId[r.parent_id]);
  const out = [];
  const walk = (p, depth) => {
    out.push([p, depth]);
    rows.filter((r) => r.parent_id === p.id).forEach((c) => walk(c, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));
  return out;
}

function PageTreeCard({ p, depth, onEdit, onAddSub, onRemove, onCopyUrl }) {
  const published = p.status === "published";
  const url = `/page/${p.slug}`;
  return (
    <>
      <motion.div key={p.id} layout style={{ marginLeft: depth * 22 }}
        className="group rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md"
        data-testid={`wxm-page-card-${p.slug}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {depth > 0 && <CornerDownRight size={12} className="shrink-0 text-slate-400"/>}
              {p.ai_generated && <Sparkles size={12} className="text-violet-500" title="AI generated"/>}
              <span className="truncate text-base font-bold text-[#0F2042]">{p.title}</span>
            </div>
            <div className="mt-0.5 truncate font-mono text-[11px] text-slate-400">/page/{p.slug}</div>
          </div>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ${published ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-slate-100 text-slate-500 ring-slate-200"}`}>
            {published ? "Live" : "Draft"}
          </span>
        </div>
        {p.meta_description && <p className="mt-2 line-clamp-2 text-[11px] text-slate-500">{p.meta_description}</p>}
        <div className="mt-3 flex items-center justify-between gap-1 border-t border-slate-100 pt-3">
          <button onClick={() => onAddSub(p)}
            data-testid={`wxm-page-addsub-${p.slug}`}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[10px] font-bold text-slate-600 hover:border-[#F97316] hover:text-[#F97316]">
            <Plus size={10}/> Subpage
          </button>
          <div className="flex items-center gap-0.5">
            <button onClick={() => onCopyUrl(p.slug)} title="Copy URL" data-testid={`wxm-page-copy-${p.slug}`}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#0F2042]"><Copy size={13}/></button>
            {published && (
              <a href={url} target="_blank" rel="noreferrer" title="Open live page" data-testid={`wxm-page-open-${p.slug}`}
                className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50"><ExternalLink size={13}/></a>
            )}
            <button onClick={() => onEdit(p)} title="Edit" data-testid={`wxm-page-edit-${p.slug}`}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#0F2042]"><Pencil size={13}/></button>
            <button onClick={() => onRemove(p.id)} title="Delete" data-testid={`wxm-page-delete-${p.slug}`}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 size={13}/></button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

export function PagesPanel({ rows, themes, refresh }) {
  const [editing, setEditing] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Delete this page? Visitors hitting the URL will see a 404. Subpages are kept (they become top-level).")) return;
    try { await api.delete(`/wxm/pages/${id}`); toast.success("Page deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/page/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success(`Copied · ${url}`);
  };

  const addSub = (p) => setEditing({ _isNew: true, parent_id: p.id, slugPrefix: p.slug });

  return (
    <div className="space-y-3" data-testid="wxm-pages-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          Pages & subpages live at <span className="font-mono text-[#F97316]">/page/{"<custom-url>"}</span> — build them fully manually, with drag-drop sections, or let AI draft a starting point.
        </div>
        <button onClick={() => setEditing({ _isNew: true })} data-testid="wxm-new-page"
          className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#F97316] to-[#A855F7] px-4 py-1.5 text-xs font-bold text-white shadow hover:opacity-90">
          <Plus size={12}/> New page
        </button>
      </div>

      {rows.length === 0 ? (
        <EmptyHint title="No pages yet" hint="Click 'New page' — write it yourself section by section, or use the optional AI draft."/>
      ) : (
        <div className="space-y-3">
          {flatTree(rows).map(([p, depth]) => (
            <PageTreeCard key={p.id} p={p} depth={depth}
              onEdit={(pp) => setEditing(pp)}
              onAddSub={addSub}
              onRemove={remove}
              onCopyUrl={copyUrl}/>
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <PageEditor
            page={editing._isNew ? null : editing}
            seed={editing._isNew ? editing : null}
            pages={rows}
            themes={themes}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); refresh(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

const SECTION_TYPES = [
  { type: "intro", label: "Text block", blank: () => ({ type: "intro", heading: "", body: "" }) },
  { type: "features", label: "Features grid", blank: () => ({ type: "features", heading: "", items: [{ title: "", description: "" }] }) },
  { type: "process", label: "Process steps", blank: () => ({ type: "process", heading: "", steps: [{ title: "", description: "" }] }) },
  { type: "faq", label: "FAQ", blank: () => ({ type: "faq", heading: "", items: [{ q: "", a: "" }] }) },
  { type: "proof", label: "Proof / quote", blank: () => ({ type: "proof", heading: "", body: "" }) },
  { type: "media", label: "Image / Video", blank: () => ({ type: "media", heading: "", body: "", media_url: "", layout: "right" }) },
];

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900";

function SectionEditor({ s, index, total, onChange, onRemove, onMove, onDragStart, onDropOn }) {
  const set = (k, v) => onChange({ ...s, [k]: v });
  const listKey = s.type === "process" ? "steps" : "items";
  const list = s[listKey] || [];
  const setItem = (i, k, v) => set(listKey, list.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const blankItem = s.type === "faq" ? { q: "", a: "" } : { title: "", description: "" };
  return (
    <li draggable data-testid={`wxm-section-${index}`}
      onDragStart={onDragStart}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); onDropOn(); }}
      className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="cursor-grab text-slate-400 active:cursor-grabbing" title="Drag to reorder"><GripVertical size={14}/></span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-600">{s.type}</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => onMove(-1)} disabled={index === 0} title="Move up" data-testid={`wxm-section-up-${index}`}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronUp size={13}/></button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} title="Move down" data-testid={`wxm-section-down-${index}`}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronDown size={13}/></button>
          <button onClick={onRemove} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-500" data-testid={`wxm-section-del-${index}`}><Trash2 size={12}/></button>
        </div>
      </div>
      <div className="mt-2 space-y-2">
        <input value={s.heading || ""} onChange={(e) => set("heading", e.target.value)} placeholder="Section heading"
          data-testid={`wxm-section-heading-${index}`} className={inputCls}/>
        {(s.type === "intro" || s.type === "proof") && (
          <textarea rows={3} value={s.body || ""} onChange={(e) => set("body", e.target.value)} placeholder="Body text…"
            data-testid={`wxm-section-body-${index}`} className={inputCls}/>
        )}
        {s.type === "media" && (
          <div className="space-y-2">
            <input value={s.media_url || ""} onChange={(e) => set("media_url", e.target.value)}
              placeholder="Image URL, video URL (.mp4) or YouTube/Vimeo link — plays right on the page"
              data-testid={`wxm-section-media-url-${index}`} className={inputCls}/>
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-bold text-slate-700 hover:border-[#F97316] hover:text-[#F97316]">
                Upload image
                <input type="file" accept="image/*" className="hidden" data-testid={`wxm-section-media-upload-${index}`}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 2.5 * 1024 * 1024) { toast.error("Image too large — keep it under 2.5 MB or paste a URL"); return; }
                    const r = new FileReader();
                    r.onload = () => set("media_url", r.result);
                    r.readAsDataURL(f);
                  }}/>
              </label>
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Media position:</span>
              {["left", "right", "top", "bottom"].map((pos) => (
                <button key={pos} onClick={() => set("layout", pos)} data-testid={`wxm-media-layout-${pos}-${index}`}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase transition ${(s.layout || "right") === pos ? "bg-[#0F2042] text-white" : "border border-slate-300 bg-white text-slate-600 hover:border-[#0F2042]"}`}>
                  {pos}
                </button>
              ))}
            </div>
            <textarea rows={3} value={s.body || ""} onChange={(e) => set("body", e.target.value)}
              placeholder="Text shown beside/above/below the media (optional)"
              data-testid={`wxm-section-body-${index}`} className={inputCls}/>
            {s.media_url && (
              <div className="max-w-[280px]" data-testid={`wxm-media-thumb-${index}`}>
                <MediaEmbed url={s.media_url}/>
                <div className="mt-1 text-[10px] text-slate-400">Detected: {mediaKind(s.media_url)} · shown {s.layout || "right"} of the text</div>
              </div>
            )}
          </div>
        )}
        {(s.type === "features" || s.type === "process" || s.type === "faq") && (
          <div className="space-y-2">
            {list.map((it, i) => (
              <div key={i} className="rounded-lg bg-slate-50 p-2">
                <div className="flex items-center gap-2">
                  <input value={s.type === "faq" ? (it.q || "") : (it.title || "")}
                    onChange={(e) => setItem(i, s.type === "faq" ? "q" : "title", e.target.value)}
                    placeholder={s.type === "faq" ? "Question" : "Title"} className={inputCls}/>
                  <button onClick={() => set(listKey, list.filter((_, idx) => idx !== i))}
                    className="rounded p-1 text-slate-400 hover:text-rose-500"><Trash2 size={11}/></button>
                </div>
                <textarea rows={2} value={s.type === "faq" ? (it.a || "") : (it.description || "")}
                  onChange={(e) => setItem(i, s.type === "faq" ? "a" : "description", e.target.value)}
                  placeholder={s.type === "faq" ? "Answer" : "Description"} className={`mt-1.5 ${inputCls}`}/>
              </div>
            ))}
            <button onClick={() => set(listKey, [...list, { ...blankItem }])}
              className="rounded-full border border-dashed border-slate-300 px-3 py-1 text-[10px] font-bold text-slate-500 hover:border-[#F97316] hover:text-[#F97316]">
              + Add {s.type === "faq" ? "question" : s.type === "process" ? "step" : "item"}
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

const EMPTY_PAGE = {
  title: "", slug: "", meta_description: "", meta_keywords: [],
  hero_eyebrow: "", hero_headline: "", hero_subhead: "", hero_image_url: "",
  cta_label: "", cta_url: "", theme_id: "", sections: [], parent_id: "",
};

export function PageEditor({ page, themes, pages = [], seed = null, onClose, onSaved }) {
  const isNew = !page;
  const [form, setForm] = useState(page
    ? { ...page, meta_keywords: page.meta_keywords || [] }
    : { ...EMPTY_PAGE, parent_id: seed?.parent_id || "", slug: seed?.slugPrefix ? `${seed.slugPrefix}/` : "" });
  const dragIdx = useRef(null);
  const moveSection = (from, to) => {
    if (from == null || to == null || from === to) return;
    setForm((f) => {
      const arr = [...(f.sections || [])];
      if (from < 0 || from >= arr.length || to < 0 || to >= arr.length) return f;
      const [x] = arr.splice(from, 1);
      arr.splice(to, 0, x);
      return { ...f, sections: arr };
    });
    dragIdx.current = null;
  };
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState({ topic: "", audience: "engineering decision-makers", tone: "confident · concise · technical", primary_keyword: "" });
  const [showPreview, setShowPreview] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setKeyword = (v) =>
    setField("meta_keywords", v.split(",").map((x) => x.trim()).filter(Boolean));

  const aiDraft = async () => {
    if (!aiPrompt.topic.trim()) return toast.error("Add a topic — e.g. 'AI staff augmentation for fintech'");
    setAiBusy(true);
    let toastId;
    try {
      toastId = toast.loading("AI is drafting your page… (≈ 8s)");
      const { data } = await api.post("/wxm/pages/ai-draft", aiPrompt, { timeout: 60000 });
      if (!data || !data.title) throw new Error("Empty AI response");
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        // Prefer AI slug for a brand-new (no-id) page; keep existing slug on re-draft.
        slug: f.id ? (f.slug || data.slug || "") : (data.slug || f.slug || ""),
        meta_description: data.meta_description || f.meta_description,
        meta_keywords: data.meta_keywords?.length ? data.meta_keywords : f.meta_keywords,
        hero_eyebrow: data.hero_eyebrow || f.hero_eyebrow,
        hero_headline: data.hero_headline || f.hero_headline,
        hero_subhead: data.hero_subhead || f.hero_subhead,
        cta_label: data.cta_label || f.cta_label,
        sections: (data.sections && data.sections.length) ? data.sections : f.sections,
        ai_generated: true,
      }));
      toast.success("AI drafted — review and tweak", { id: toastId });
    } catch (e) {
      toast.error(formatApiError(e) || "AI drafting failed — please retry", { id: toastId });
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return null; }
    setBusy(true);
    try {
      if (isNew && !form.id) {
        const { data } = await api.post("/wxm/pages", form);
        toast.success("Page saved as draft");
        setForm(data);
        return data;
      }
      const id = form.id;
      const { data } = await api.patch(`/wxm/pages/${id}`, form);
      setForm(data);
      toast.success("Page updated");
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
    finally { setBusy(false); }
  };

  const publish = async () => {
    const saved = await save();
    if (!saved?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/wxm/pages/${saved.id}/publish`);
      setForm(data);
      toast.success("Published — your page is now live");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const unpublish = async () => {
    if (!form.id) return;
    if (!window.confirm("Take this page offline?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/wxm/pages/${form.id}/unpublish`);
      setForm(data);
      toast.success("Page unpublished");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const livePath = form.slug ? `/page/${form.slug}` : "";
  const published = form.status === "published";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="wxm-page-editor"
      className="fixed inset-0 z-[70] flex items-stretch justify-end bg-slate-950/60 backdrop-blur-sm"
      onClick={onClose}>
      <motion.div initial={{ x: 80 }} animate={{ x: 0 }} exit={{ x: 80 }} transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white p-4">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wide text-[#F97316]"><FileText size={10} className="inline mr-1"/> {isNew && !form.id ? "Create page" : "Edit page"}</div>
            <h3 className="font-display truncate text-lg font-bold text-[#0F2042]">{form.title || "Untitled page"}</h3>
            {livePath && (
              <a href={livePath} target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-[#F97316]">
                {window.location.origin}{livePath} {published && <ExternalLink size={9} className="inline ml-0.5"/>}
              </a>
            )}
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" data-testid="wxm-page-editor-close"><X size={18}/></button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-4 py-2">
          <button onClick={() => setShowPreview(true)} disabled={!form.title} data-testid="wxm-page-preview"
            className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[11px] font-bold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100 disabled:opacity-40">
            <Eye size={11}/> Preview & Save
          </button>
          <button onClick={save} disabled={busy} data-testid="wxm-page-save"
            className="inline-flex items-center gap-1 rounded-full bg-slate-700 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-slate-800 disabled:opacity-50">
            {busy ? <Loader2 size={11} className="animate-spin"/> : <Save size={11}/>} Save draft
          </button>
          {published ? (
            <button onClick={unpublish} disabled={busy} data-testid="wxm-page-unpublish"
              className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-3 py-1.5 text-[11px] font-bold text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100 disabled:opacity-50">
              Unpublish
            </button>
          ) : (
            <button onClick={publish} disabled={busy} data-testid="wxm-page-publish"
              className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white shadow hover:opacity-90 disabled:opacity-50">
              {busy ? <Loader2 size={11} className="animate-spin"/> : <Send size={11}/>} Publish
            </button>
          )}
          {form.id && form.slug && (
            <a href={livePath} target="_blank" rel="noreferrer" data-testid="wxm-page-open-url"
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-violet-100 px-3 py-1.5 text-[11px] font-bold text-violet-700 hover:bg-violet-200">
              <ExternalLink size={11}/> Open URL
            </a>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-orange-50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-violet-700">
                <Sparkles size={12}/> AI assistant · drafts SEO-optimised content
              </div>
              {form.ai_generated && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[9px] font-bold text-violet-700">AI generated</span>}
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <input value={aiPrompt.topic} onChange={(e) => setAiPrompt({ ...aiPrompt, topic: e.target.value })}
                placeholder="Topic — e.g. 'AI staff augmentation for fintech'" data-testid="wxm-ai-topic"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"/>
              <input value={aiPrompt.primary_keyword} onChange={(e) => setAiPrompt({ ...aiPrompt, primary_keyword: e.target.value })}
                placeholder="Primary keyword (optional)" data-testid="wxm-ai-keyword"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"/>
              <input value={aiPrompt.audience} onChange={(e) => setAiPrompt({ ...aiPrompt, audience: e.target.value })}
                placeholder="Audience" className="rounded-lg border border-slate-300 px-3 py-2 text-sm"/>
              <input value={aiPrompt.tone} onChange={(e) => setAiPrompt({ ...aiPrompt, tone: e.target.value })}
                placeholder="Tone" className="rounded-lg border border-slate-300 px-3 py-2 text-sm"/>
            </div>
            <button onClick={aiDraft} disabled={aiBusy} data-testid="wxm-ai-draft"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-1.5 text-xs font-bold text-white shadow disabled:opacity-50">
              {aiBusy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
              {aiBusy ? "Drafting…" : "Draft with AI"}
            </button>
            <div className="mt-2 text-[10px] text-slate-500">Fills title, meta description, hero, features, FAQ — all production-ready and SEO-tuned.</div>
          </div>

          <PageSection heading="SEO & identity">
            <PageField label="Title *">
              <input value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid="wxm-page-title" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/>
            </PageField>
            <PageField label="Custom URL (supports subpaths, e.g. services/web-design)" hint={form.slug ? `Live at /page/${form.slug}` : "Auto-derived from title"}>
              <input value={form.slug} onChange={(e) => setField("slug", e.target.value)} data-testid="wxm-page-slug" placeholder="auto — or your own like services/web-design" className={`font-mono ${inputCls}`}/>
            </PageField>
            <PageField label="Parent page (WordPress-style hierarchy)">
              <select value={form.parent_id || ""} onChange={(e) => setField("parent_id", e.target.value)} data-testid="wxm-page-parent" className={inputCls}>
                <option value="">— None (top-level page) —</option>
                {pages.filter((pp) => pp.id !== form.id).map((pp) => (
                  <option key={pp.id} value={pp.id}>{pp.title} (/page/{pp.slug})</option>
                ))}
              </select>
            </PageField>
            <PageField label="Meta description" hint={`${(form.meta_description || "").length}/280 — aim 150–160 for best SEO`}>
              <textarea rows={2} value={form.meta_description} onChange={(e) => setField("meta_description", e.target.value)} data-testid="wxm-page-meta-desc" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/>
            </PageField>
            <PageField label="Meta keywords (comma-separated)">
              <input value={(form.meta_keywords || []).join(", ")} onChange={(e) => setKeyword(e.target.value)} data-testid="wxm-page-keywords" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/>
            </PageField>
          </PageSection>

          <PageSection heading="Hero">
            <PageField label="Eyebrow"><input value={form.hero_eyebrow} onChange={(e) => setField("hero_eyebrow", e.target.value)} data-testid="wxm-page-eyebrow" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/></PageField>
            <PageField label="Headline"><input value={form.hero_headline} onChange={(e) => setField("hero_headline", e.target.value)} data-testid="wxm-page-headline" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/></PageField>
            <PageField label="Subhead"><textarea rows={2} value={form.hero_subhead} onChange={(e) => setField("hero_subhead", e.target.value)} data-testid="wxm-page-subhead" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/></PageField>
            <PageField label="Hero image URL (optional)"><input value={form.hero_image_url} onChange={(e) => setField("hero_image_url", e.target.value)} placeholder="https://…" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/></PageField>
            <div className="grid gap-3 sm:grid-cols-2">
              <PageField label="CTA label"><input value={form.cta_label} onChange={(e) => setField("cta_label", e.target.value)} data-testid="wxm-page-cta-label" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/></PageField>
              <PageField label="CTA URL"><input value={form.cta_url} onChange={(e) => setField("cta_url", e.target.value)} placeholder="/contact" data-testid="wxm-page-cta-url" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/></PageField>
            </div>
            <PageField label="Theme">
              <select value={form.theme_id || ""} onChange={(e) => setField("theme_id", e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                <option value="">— Default Projexino —</option>
                {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </PageField>
          </PageSection>

          <PageSection heading="Content sections" subline="Build them manually here — add, edit, drag to reorder. AI drafting above is optional.">
            <div className="flex flex-wrap gap-1.5">
              {SECTION_TYPES.map((t) => (
                <button key={t.type} onClick={() => setField("sections", [...(form.sections || []), t.blank()])}
                  data-testid={`wxm-add-section-${t.type}`}
                  className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-bold text-slate-700 hover:border-[#F97316] hover:text-[#F97316]">
                  + {t.label}
                </button>
              ))}
            </div>
            {(form.sections || []).length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white p-6 text-center text-xs text-slate-500">
                No sections yet — add one above, or use <b>Draft with AI</b>.
              </div>
            ) : (
              <ul className="space-y-2">
                {(form.sections || []).map((sec, i) => (
                  <SectionEditor key={i} s={sec} index={i} total={form.sections.length}
                    onDragStart={() => { dragIdx.current = i; }}
                    onDropOn={() => moveSection(dragIdx.current, i)}
                    onChange={(ns) => setField("sections", form.sections.map((x, idx) => (idx === i ? ns : x)))}
                    onMove={(dir) => moveSection(i, i + dir)}
                    onRemove={() => setField("sections", form.sections.filter((_, idx) => idx !== i))}/>
                ))}
              </ul>
            )}
          </PageSection>
        </div>
      </motion.div>

      <AnimatePresence>
        {showPreview && (
          <PagePreviewModal form={form} onClose={() => setShowPreview(false)} onSave={async () => { await save(); setShowPreview(false); }}/>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function PageSection({ heading, subline, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{heading}</div>
      {subline && <div className="text-[11px] text-slate-500">{subline}</div>}
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );
}
function PageField({ label, hint, children }) {
  return (
    <label className="block">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
        {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function PagePreviewModal({ form, onClose, onSave }) {
  const livePath = form.slug ? `/page/${form.slug}` : "";
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="wxm-page-preview-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/80 backdrop-blur-md p-4"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 p-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500"><BookOpenCheck size={10} className="inline mr-1"/> Preview · {form.title || "Untitled"}</div>
            <div className="text-[11px] text-slate-400 font-mono">{livePath || "(slug auto-derived on save)"}</div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={onSave} data-testid="wxm-page-preview-save" className="inline-flex items-center gap-1 rounded-full bg-slate-700 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-slate-800">
              <Save size={11}/> Save & keep editing
            </button>
            <button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X size={18}/></button>
          </div>
        </div>
        <iframe title="page-preview" srcDoc={renderPreviewHtml(form)} sandbox="allow-same-origin"
          className="flex-1 w-full bg-white"/>
      </motion.div>
    </motion.div>
  );
}

function renderPreviewHtml(form) {
  const primary = "#F97316", accent = "#A855F7", bg = "#0F2042";
  const escape = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]));
  const sectionsHtml = (form.sections || []).map((s) => {
    if (s.type === "intro" || s.type === "proof") {
      return `<section style="margin:80px auto;max-width:880px;padding:0 24px">
        ${s.heading ? `<h2 style="font-size:34px;font-weight:700;color:${bg};margin:0 0 16px">${escape(s.heading)}</h2>` : ""}
        ${s.body ? `<p style="font-size:18px;line-height:1.65;color:#334155;max-width:720px">${escape(s.body)}</p>` : ""}
      </section>`;
    }
    if (s.type === "features") {
      const items = (s.items || []).map((it, i) => `
        <div style="border:1px solid #E2E8F0;border-radius:18px;padding:24px;background:#fff">
          <div style="display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;color:#fff;font-weight:700;background:linear-gradient(135deg,${primary},${accent})">${i + 1}</div>
          <h3 style="margin:16px 0 6px;color:${bg};font-size:18px;font-weight:700">${escape(it.title)}</h3>
          <p style="margin:0;color:#475569;font-size:14px;line-height:1.6">${escape(it.description)}</p>
        </div>`).join("");
      return `<section style="margin:80px auto;max-width:1040px;padding:0 24px">
        ${s.heading ? `<h2 style="font-size:34px;font-weight:700;color:${bg};margin:0 0 28px">${escape(s.heading)}</h2>` : ""}
        <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:20px">${items}</div>
      </section>`;
    }
    if (s.type === "process") {
      const steps = (s.steps || []).map((st, i) => `
        <div style="border:1px solid #E2E8F0;background:linear-gradient(135deg,#FFF7ED,#F5F3FF);border-radius:18px;padding:24px">
          <div style="font-size:11px;font-weight:700;letter-spacing:0.22em;text-transform:uppercase;color:${primary}">Step ${i + 1}</div>
          <h3 style="margin:8px 0 6px;color:${bg};font-size:18px;font-weight:700">${escape(st.title)}</h3>
          <p style="margin:0;color:#475569;font-size:14px;line-height:1.6">${escape(st.description)}</p>
        </div>`).join("");
      return `<section style="margin:80px auto;max-width:1040px;padding:0 24px">
        ${s.heading ? `<h2 style="font-size:34px;font-weight:700;color:${bg};margin:0 0 28px">${escape(s.heading)}</h2>` : ""}
        <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px">${steps}</div>
      </section>`;
    }
    if (s.type === "faq") {
      const faq = (s.items || []).map((q) => `
        <details style="border:1px solid #E2E8F0;border-radius:14px;padding:16px 18px;background:#fff;margin-bottom:8px">
          <summary style="cursor:pointer;font-weight:600;color:${bg};font-size:16px">${escape(q.q)}</summary>
          <p style="margin:10px 0 0;color:#475569;font-size:14px;line-height:1.6">${escape(q.a)}</p>
        </details>`).join("");
      return `<section style="margin:80px auto;max-width:880px;padding:0 24px">
        ${s.heading ? `<h2 style="font-size:34px;font-weight:700;color:${bg};margin:0 0 20px">${escape(s.heading)}</h2>` : ""}
        ${faq}
      </section>`;
    }
    if (s.type === "media" && s.media_url) {
      const media = mediaHtml(s.media_url);
      const text = `${s.heading ? `<h2 style="font-size:30px;font-weight:700;color:${bg};margin:0 0 12px">${escape(s.heading)}</h2>` : ""}
        ${s.body ? `<p style="font-size:17px;line-height:1.65;color:#334155;margin:0">${escape(s.body)}</p>` : ""}`;
      const layout = s.layout || "right";
      if (layout === "left" || layout === "right") {
        const cols = layout === "left" ? [media, `<div>${text}</div>`] : [`<div>${text}</div>`, media];
        return `<section style="margin:80px auto;max-width:1040px;padding:0 24px">
          <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:36px;align-items:center">
            <div>${cols[0]}</div><div>${cols[1]}</div>
          </div></section>`;
      }
      const stack = layout === "top" ? [media, text] : [text, media];
      return `<section style="margin:80px auto;max-width:880px;padding:0 24px">
        <div style="margin-bottom:28px">${stack[0]}</div><div>${stack[1]}</div></section>`;
    }
    return "";
  }).join("");

  return `<!doctype html>
<html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${escape(form.title || "Preview")}</title>
<style>body{margin:0;font-family:Inter,Arial,sans-serif;color:#0f172a}</style></head><body>
<header style="background:linear-gradient(140deg,${bg} 0%,#0a1530 80%);color:#fff;padding:100px 24px 80px">
  <div style="max-width:1040px;margin:0 auto">
    ${form.hero_eyebrow ? `<div style="display:inline-block;padding:6px 14px;border-radius:999px;background:rgba(255,255,255,0.1);color:${primary};font-size:11px;font-weight:700;letter-spacing:0.22em;text-transform:uppercase">${escape(form.hero_eyebrow)}</div>` : ""}
    <h1 style="margin:18px 0 12px;font-size:56px;font-weight:700;line-height:1.08;max-width:760px">${escape(form.hero_headline || form.title || "Untitled page")}</h1>
    ${form.hero_subhead ? `<p style="font-size:18px;line-height:1.6;color:#cbd5e1;max-width:680px">${escape(form.hero_subhead)}</p>` : ""}
    ${form.cta_label ? `<a href="${escape(form.cta_url || "#")}" style="display:inline-block;margin-top:28px;padding:14px 30px;border-radius:999px;background:linear-gradient(135deg,${primary},${accent});color:#fff;font-weight:700;text-decoration:none">${escape(form.cta_label)} →</a>` : ""}
  </div>
</header>
${sectionsHtml}
${form.cta_label ? `<section style="background:#F8FAFC;border-top:1px solid #E2E8F0;padding:64px 24px;text-align:center">
  <h3 style="font-size:32px;font-weight:700;color:${bg};margin:0">Ready when you are.</h3>
  <p style="color:#475569;margin:10px 0 20px">${escape(form.hero_subhead || "Talk to the team.")}</p>
  <a href="${escape(form.cta_url || "#")}" style="display:inline-block;padding:14px 30px;border-radius:999px;background:linear-gradient(135deg,${primary},${accent});color:#fff;font-weight:700;text-decoration:none">${escape(form.cta_label)} →</a>
</section>` : ""}
<footer style="padding:24px;text-align:center;color:#94a3b8;font-size:11px">© ${new Date().getFullYear()} Projexino</footer>
</body></html>`;
}
